import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import {
  HeatTraceActionTypes,
  HeatTraceLoad,
  HeatTraceSearch,
  HeatTraceLoadFailed,
  HeatTraceLoadCircuits,
  HeatTraceLoadCircuitsSuccess,
  HeatTraceLoadInsulation,
  HeatTraceLoadInsulationSuccess,
  HeatTraceLoadOverview,
  HeatTraceLoadOverviewSuccess,
  HeatTraceLoadSuccess,
} from '@completion/actions';
import {
  HeatTraceCircuit,
  HeatTraceInsulationResponse,
  HeatTraceOverview,
  HeatTraceResponse,
  HeatTraceTest,
  HeatTraceTests,
  Project,
} from '@completion/models';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { HeatTraceService } from '@completion/services';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class HeatTraceEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly heatTraceService: HeatTraceService,
    private readonly store: Store<State>,
  ) {}

  loadHeatTrace$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HeatTraceLoad>(HeatTraceActionTypes.HeatTraceLoad),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HeatTraceResponse>({
        actions$: this.actions$,
        store: this.store,
        service: this.heatTraceService,
        method: 'getHeatTrace',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.week, action.date],
        onSuccess: (response) => new HeatTraceLoadSuccess(this.extractHeatTraceWeeks(response)),
        onError: (error) => new HeatTraceLoadFailed(this.extractHeatTraceMessage(error)),
      }),
    ),
  );

  searchHeatTrace$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HeatTraceSearch>(HeatTraceActionTypes.HeatTraceSearch),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HeatTraceResponse>({
        actions$: this.actions$,
        store: this.store,
        service: this.heatTraceService,
        method: 'searchHeatTraceTests',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.query],
        onSuccess: (response) => new HeatTraceLoadSuccess(this.extractHeatTraceWeeks(response)),
        onError: (error) => new HeatTraceLoadFailed(this.extractHeatTraceMessage(error)),
      }),
    ),
  );

  private extractHeatTraceWeeks(response: unknown): HeatTraceTests[] {
    if (Array.isArray(response)) {
      if (!response.length) {
        return [];
      }

      // Search endpoint may return either week groups or a flat list of tests.
      if (this.isWeekGroup(response[0])) {
        return response as HeatTraceTests[];
      }

      if (this.isPipeTest(response[0])) {
        return [this.wrapFlatTestsAsWeek(response as HeatTraceTest[])];
      }
    }

    const payload = response as (HeatTraceResponse & { tests?: HeatTraceTest[] }) | null | undefined;
    if (payload && Array.isArray(payload.weeks)) {
      return payload.weeks;
    }

    if (payload && Array.isArray(payload.tests)) {
      return [this.wrapFlatTestsAsWeek(payload.tests)];
    }

    return [];
  }

  private wrapFlatTestsAsWeek(tests: HeatTraceTest[]): HeatTraceTests {
    const currentDate = new Date();
    return {
      week: 0,
      weekYear: 'Search',
      year: currentDate.getFullYear(),
      tests,
    };
  }

  private isWeekGroup(value: unknown): value is HeatTraceTests {
    const item = value as HeatTraceTests;
    return !!item && Array.isArray(item.tests) && typeof item.year === 'number';
  }

  private isPipeTest(value: unknown): value is HeatTraceTest {
    const item = value as HeatTraceTest;
    return !!item && typeof item.tagNumber === 'string';
  }

  private extractHeatTraceMessage(error: unknown): string {
    const err = error as { status?: number; error?: unknown; message?: string };
    const payload = err?.error as { message?: string; error?: string } | string | undefined;

    if (typeof payload === 'object' && payload?.message) {
      return payload.message;
    }
    if (typeof payload === 'string' && payload.trim()) {
      return payload;
    }

    if (err?.status === 404) {
      return err.message ?? 'Heat trace tests were not found.';
    }

    return err?.message ?? 'Unable to load heat trace tests.';
  }

  loadHeatTraceCircuits$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HeatTraceLoadCircuits>(HeatTraceActionTypes.HeatTraceLoadCircuits),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HeatTraceCircuit>({
        actions$: this.actions$,
        store: this.store,
        service: this.heatTraceService,
        method: 'getHeatTraceCircuits',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.tagNumber],
        onSuccess: (circuits) => new HeatTraceLoadCircuitsSuccess(circuits),
      }),
    ),
  );

  loadHeatTraceInsulation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HeatTraceLoadInsulation>(HeatTraceActionTypes.HeatTraceLoadInsulation),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HeatTraceInsulationResponse>({
        actions$: this.actions$,
        store: this.store,
        service: this.heatTraceService,
        method: 'getHeatTraceInsulation',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.tagNumber],
        onSuccess: (insulation) => new HeatTraceLoadInsulationSuccess(insulation),
      }),
    ),
  );

  loadHeatTraceOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HeatTraceLoadOverview>(HeatTraceActionTypes.HeatTraceLoadOverview),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HeatTraceOverview>({
        actions$: this.actions$,
        store: this.store,
        service: this.heatTraceService,
        method: 'getHeatTraceOverview',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.tagNumber],
        onSuccess: (overview) => new HeatTraceLoadOverviewSuccess(overview),
      }),
    ),
  );
}
